import { computed, Ref, ref } from "vue"
import { isSupported } from "firebase/messaging"
import { rejectAfter } from "helpers/promiseTimeout"
import { VueCookies } from "vue-cookies"

export const useNotifications = () => {
  const isPushSupported = ref(false)
  isSupported().then((v) => (isPushSupported.value = v))

  // Chrome has a feature called "quieter notifications" that prevents the
  // promise from Notification.requestPermission() from settling.
  // https://blog.chromium.org/2020/01/introducing-quieter-permission-ui-for.html
  const isNotificationPermissionMuted = ref(false)

  const notificationAvailable = "Notification" in window

  const notificationPermissionStatus: Ref<NotificationPermission | "quiet"> =
    ref(notificationAvailable ? Notification.permission : "denied")

  if (navigator?.serviceWorker) {
    navigator.serviceWorker
      .getRegistration()
      .then(
        () =>
          (notificationPermissionStatus.value = notificationAvailable
            ? Notification.permission
            : "denied")
      )
  }

  const isNotificationPermissionGranted = computed(() => {
    return notificationPermissionStatus.value === "granted"
  })

  const isNotificationPermissionDenied = computed(() => {
    return notificationPermissionStatus.value === "denied"
  })

  const isNotificationPermissionRequired = computed(() => {
    return (
      notificationPermissionStatus.value === "default" &&
      !isNotificationPermissionMuted.value
    )
  })

  const requestNotificationPermission = async (): Promise<boolean> => {
    try {
      if (!notificationAvailable) return isNotificationPermissionGranted.value
      try {
        notificationPermissionStatus.value = await rejectAfter(
          5000,
          Notification.requestPermission().then(
            (eventuallyReceivedPermission) => {
              console.log(
                "eventuallyReceivedPermission",
                eventuallyReceivedPermission
              )
              isNotificationPermissionMuted.value = false
              notificationPermissionStatus.value = eventuallyReceivedPermission
              return eventuallyReceivedPermission
            }
          ),
          () => (isNotificationPermissionMuted.value = true)
        )
      } catch (e) {
        notificationPermissionStatus.value = "quiet"
        console.warn("Permission request timed out, denied: ")
      }
    } catch (error) {
      console.error(error)
    }
    return isNotificationPermissionGranted.value
  }

  const reloadNotificationPermissions = async () => {
    isPushSupported.value = await isSupported()
    notificationPermissionStatus.value = notificationAvailable
      ? Notification.permission
      : "denied"
  }

  return {
    isPushSupported,
    notificationPermissionStatus,
    isNotificationPermissionMuted,
    isNotificationPermissionGranted,
    isNotificationPermissionDenied,
    isNotificationPermissionRequired,
    reloadNotificationPermissions,
    requestNotificationPermission,
  }
}
